import { ReactNode, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Breadcrumb from './Breadcrumb'

interface BreadcrumbItem {
  label: string
  href?: string
}

interface AppLayoutProps {
  children: ReactNode
  breadcrumbs?: BreadcrumbItem[]
  title?: string
  headerActions?: ReactNode
}

export default function AppLayout({
  children,
  breadcrumbs,
  title,
  headerActions,
}: AppLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()

  // Determine help link based on current route
  const isOnInstructions = location.pathname.endsWith('/instructions')

  const getHelpLink = () => {
    if (location.pathname.startsWith('/test-case-updater')) {
      return isOnInstructions ? '/test-case-updater/' : '/test-case-updater/instructions'
    } else if (location.pathname.startsWith('/test-case')) {
      return isOnInstructions ? '/test-case/' : '/test-case/instructions'
    } else if (location.pathname.startsWith('/offers-audit')) {
      return isOnInstructions ? '/offers-audit/' : '/offers-audit/instructions'
    } else if (location.pathname.startsWith('/risk-prioritization')) {
      return isOnInstructions ? '/risk-prioritization/' : '/risk-prioritization/instructions'
    } else if (location.pathname.startsWith('/rt-audit')) {
      return isOnInstructions ? '/rt-audit/' : '/rt-audit/instructions'
    }
    return null
  }

  const helpLink = getHelpLink()

  return (
    <div className="min-h-screen bg-linear-bg">
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile Header */}
      <div className="md:hidden">
        <header className="h-[52px] bg-linear-bg-sidebar border-b border-linear-border flex items-center justify-between px-4">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 -ml-2 text-linear-text-muted hover:text-linear-text transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              {mobileMenuOpen ? (
                <path d="M5 5l10 10M5 15L15 5" strokeLinecap="round" />
              ) : (
                <path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round" />
              )}
            </svg>
          </button>
          <span className="text-[13px] font-medium text-linear-text">Five & Done QA</span>
          <div className="w-9" />
        </header>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="absolute left-0 top-0 bottom-0 w-[220px] animate-slide-in">
              <Sidebar />
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <main className="md:ml-[220px] min-h-screen">
        {/* Top Bar with Breadcrumbs */}
        {(breadcrumbs || title || headerActions || helpLink) && (
          <header className="h-[52px] bg-linear-bg-sidebar border-b border-linear-border flex items-center justify-between px-5 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              {breadcrumbs && <Breadcrumb items={breadcrumbs} />}
              {title && !breadcrumbs && (
                <h1 className="text-[13px] font-medium text-linear-text">{title}</h1>
              )}
            </div>
            <div className="flex items-center gap-2">
              {headerActions}
              {helpLink && (
                <a
                  href={helpLink}
                  className="p-2 rounded-lg text-linear-text-secondary hover:text-linear-text hover:bg-linear-bg-hover transition-colors"
                  title={isOnInstructions ? 'Close Guide' : 'View Guide'}
                  aria-label={isOnInstructions ? 'Close guide' : 'View guide'}
                >
                  {isOnInstructions ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M9 9.5a3 3 0 1 1 3.75 2.904V13.5" strokeLinecap="round" />
                      <circle cx="12" cy="16.5" r="0.75" fill="currentColor" stroke="none" />
                    </svg>
                  )}
                </a>
              )}
            </div>
          </header>
        )}

        {/* Page Content */}
        <div className="p-5">
          {children}
        </div>
      </main>
    </div>
  )
}
